import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { FormProvider } from 'react-hook-form';
import { ActorRefFrom } from 'xstate';
import { FormLayout } from '~/components/forms/FormLayout';
import { AddressForm } from '~/components/forms/GenericForms/AddressForm';
import { AddressFormValues, addressSchemaOptional } from '~/features/types';
import { transformFormValuesToAddress } from '~/features/utils';
import { useCustomForm } from '~/hooks/useCustomForm';
import { PeerReference } from '~/types';
import { peerRefMachine } from '../peerReferenceMachine';
import { WizardFooter } from './WizardFooter';
const TAGS = CT.profile.contactInfo.address.form;

interface WrapperProps {
  actor: ActorRefFrom<typeof peerRefMachine>;
}

export const AddressFormWrapper = (props: WrapperProps) => {
  const { actor } = props;
  const { address } = useSelector(actor, (state) => state.context);

  const methods = useCustomForm<AddressFormValues>({
    resolver: zodResolver(addressSchemaOptional),
    defaultValues: address,
  });

  const { handleSubmit } = methods;
  const { isDirty } = methods.formState;

  const onPageSubmit = (values: AddressFormValues) => {
    const partialPeerRef: Partial<PeerReference> = {
      address: transformFormValuesToAddress(values),
    };
    actor.send({ type: 'ON_WIZ_UPDATE', values: partialPeerRef });
  };

  const nextBtn = {
    text: 'Next',
    onClick: async () => {
      if (isDirty) {
        handleSubmit(onPageSubmit)();
      } else {
        const isValid = await methods.trigger();
        if (isValid) {
          actor.send({ type: 'ON_WIZ_NEXT_PAGE' });
        }
      }
    },
  };

  const prevBtn = {
    shouldShow: true,
    onClick: () => {
      actor.send({
        type: 'ON_WIZ_PREV_PAGE',
        draft: { kind: 'address', values: methods.getValues() },
      });
    },
  };

  return (
    <>
      <FormProvider {...methods}>
        <form data-cy={TAGS.form} id="address-form">
          <FormLayout className="max-w-3xl pb-8">
            <AddressForm />
          </FormLayout>
        </form>
      </FormProvider>
      <WizardFooter nextBtn={nextBtn} prevBtn={prevBtn} />
    </>
  );
};
